// Comportamenti condivisi, senza dipendenze runtime.

const fermo = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Il ridimensionamento arriva a raffica, decine di eventi per un solo gesto. Qui si
// ricalcola una volta sola quando il gesto si ferma, non a ogni evento.
const alRidimensionamento = (azione, attesa = 150) => {
  let timer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(azione, attesa);
  });
};

// Le fotografie che si alternano durante lo scorrimento devono essere già in memoria
// quando la sezione arriva sotto gli occhi, ma prima venivano forzate a `eager` all'avvio:
// sei immagini grandi che si contendevano la banda con la foto della hero, cioè con
// l'elemento che decide l'LCP. Adesso partono quando la sezione si avvicina, e comunque
// non oltre il caricamento completo della pagina.
const precaricaAllAvvicinarsi = (sezione, immagini) => {
  if (!immagini.length) return;
  immagini.forEach((foto) => { foto.decoding = 'async'; });

  if (!('IntersectionObserver' in window)) {
    immagini.forEach((foto) => { foto.loading = 'eager'; });
    return;
  }

  let osservatore = null;
  const carica = () => {
    if (immagini[0].loading === 'eager') return;
    osservatore?.disconnect();
    immagini.forEach((foto) => { foto.loading = 'eager'; });
  };

  osservatore = new IntersectionObserver(([voce]) => {
    if (voce.isIntersecting) carica();
  }, { rootMargin: '1200px 0px' });
  osservatore.observe(sezione);

  if (document.readyState === 'complete') carica();
  else window.addEventListener('load', carica, { once: true });
};

// 1. Reveal. Con reduced-motion non si osserva niente: il contenuto è già visibile da CSS.
if (!fermo && 'IntersectionObserver' in window) {
  const osservatore = new IntersectionObserver((voci) => {
    for (const voce of voci) {
      if (!voce.isIntersecting) continue;
      voce.target.classList.add('visibile');
      osservatore.unobserve(voce.target);
    }
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });

  document.querySelectorAll('.reveal, .reveal-immagine').forEach((n) => {
    const posizione = [...n.parentElement.children].indexOf(n);
    n.style.setProperty('--reveal-ritardo', Math.min(Math.max(posizione, 0), 4) * 70 + 'ms');
    osservatore.observe(n);
  });
  document.documentElement.classList.add('reveal-pronto');

  // Un salto rapido di più viewport può non intersecare mai una sezione. Le animazioni
  // accompagnano il contenuto, non ne decidono la visibilità.
  window.setTimeout(() => {
    document.querySelectorAll('.reveal, .reveal-immagine').forEach((n) => {
      if (n.getBoundingClientRect().top < window.innerHeight * 1.2) n.classList.add('visibile');
    });
  }, 2600);
}

// Su telefono i paragrafi entrano uno alla volta mentre la pagina continua a
// scorrere liberamente. Le classi vengono aggiunte solo con JavaScript attivo,
// quindi il contenuto resta sempre visibile in caso di errore o reduced motion.
if (!fermo && window.matchMedia('(max-width: 767px)').matches && 'IntersectionObserver' in window) {
  const vociMobile = [...document.querySelectorAll('.storia__testo p, .metodo__corpo p, .faq__voce')];
  const osservatoreMobile = new IntersectionObserver((voci) => {
    for (const voce of voci) {
      if (!voce.isIntersecting) continue;
      voce.target.classList.add('visibile');
      osservatoreMobile.unobserve(voce.target);
    }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

  vociMobile.forEach((voce, indice) => {
    voce.classList.add('reveal-mobile');
    voce.style.setProperty('--reveal-mobile-ritardo', `${Math.min(indice % 3, 2) * 55}ms`);
    osservatoreMobile.observe(voce);
  });
}

// Il dato sociale appartiene al racconto di Sonia. Parte da zero solo quando
// entra in scena e raggiunge il valore reale una volta, senza loop.
const contatori = [...document.querySelectorAll('[data-contatore]')];
if (!fermo && contatori.length && 'IntersectionObserver' in window) {
  const formatoNumero = new Intl.NumberFormat('it-IT');
  const osservatoreContatori = new IntersectionObserver((voci) => {
    for (const voce of voci) {
      if (!voce.isIntersecting) continue;
      osservatoreContatori.unobserve(voce.target);
      const destinazione = Number.parseInt(voce.target.dataset.target || '0', 10);
      const inizio = performance.now();
      const durata = 1300;
      const aggiornaContatore = (tempo) => {
        const progresso = Math.min(1, (tempo - inizio) / durata);
        const attenuato = 1 - Math.pow(1 - progresso, 4);
        voce.target.textContent = formatoNumero.format(Math.round(destinazione * attenuato));
        if (progresso < 1) requestAnimationFrame(aggiornaContatore);
      };
      requestAnimationFrame(aggiornaContatore);
    }
  }, { threshold: 0.55 });

  contatori.forEach((contatore) => {
    contatore.textContent = '0';
    osservatoreContatori.observe(contatore);
  });
}

const metodo = document.querySelector('.metodo-redesign');
const passiMetodo = metodo ? [...metodo.querySelectorAll('[data-passo-metodo]')] : [];
if (!fermo && passiMetodo.length) {
  metodo.classList.add('metodo-interattivo');
  const fotoMetodo = [...metodo.querySelectorAll('[data-metodo-foto]')];
  precaricaAllAvvicinarsi(metodo, fotoMetodo);
  let frameMetodo = 0;
  const attivaPasso = (indice) => {
    passiMetodo.forEach((voce, i) => voce.classList.toggle('passo--attivo', i === indice));
    metodo.style.setProperty('--metodo-progresso', String((indice + 1) / passiMetodo.length));
    if (fotoMetodo.length) {
      const indiceFoto = passiMetodo.length === 1
        ? 0
        : Math.round(indice * (fotoMetodo.length - 1) / (passiMetodo.length - 1));
      fotoMetodo.forEach((foto, i) => foto.classList.toggle('is-active', i === indiceFoto));
    }
  };
  let indiceMetodo = 0;
  const aggiornaMetodo = () => {
    frameMetodo = 0;
    const altezza = window.innerHeight;
    // Fuori campo non c'è niente da aggiornare: una misura sola invece di una per
    // passo, su ogni fotogramma di scorrimento dell'intera pagina.
    const cornice = metodo.getBoundingClientRect();
    if (cornice.bottom <= 0 || cornice.top >= altezza) return;
    const lineaLettura = altezza * 0.48;
    let indice = 0;
    passiMetodo.forEach((passo, i) => {
      if (passo.getBoundingClientRect().top <= lineaLettura) indice = i;
    });
    if (indice === indiceMetodo) return;
    indiceMetodo = indice;
    attivaPasso(indice);
  };
  const richiediMetodo = () => {
    if (frameMetodo) return;
    frameMetodo = requestAnimationFrame(aggiornaMetodo);
  };
  attivaPasso(0);
  window.addEventListener('scroll', richiediMetodo, { passive: true });
  alRidimensionamento(richiediMetodo);
  richiediMetodo();
}

// La storia procede per paragrafi: ogni fotografia sostituisce la precedente nello
// stesso fotogramma. Nessuna immagine viene appoggiata sopra un'altra.
const storia = document.querySelector('[data-storia]');
if (storia && !fermo) {
  const paragrafi = [...storia.querySelectorAll('.storia__testo p')];
  const fotoStoria = [...storia.querySelectorAll('[data-storia-foto]')];
  precaricaAllAvvicinarsi(storia, fotoStoria);
  if (paragrafi.length) {
    storia.classList.add('storia-interattiva');
    let frameStoria = 0;
    const attivaParagrafo = (indice) => {
      paragrafi.forEach((voce, i) => {
        voce.classList.toggle('is-active', i === indice);
        voce.classList.toggle('is-past', i < indice);
      });
      storia.style.setProperty('--storia-progresso', String((indice + 1) / paragrafi.length));
      const indiceFoto = paragrafi.length === 1
        ? 0
        : Math.round(indice * (fotoStoria.length - 1) / (paragrafi.length - 1));
      fotoStoria.forEach((foto, i) => foto.classList.toggle('is-active', i === indiceFoto));
    };
    let indiceStoria = 0;
    const aggiornaStoria = () => {
      frameStoria = 0;
      const altezza = window.innerHeight;
      const cornice = storia.getBoundingClientRect();
      if (cornice.bottom <= 0 || cornice.top >= altezza) return;
      const lineaLettura = altezza * 0.48;
      let indice = 0;
      paragrafi.forEach((paragrafo, i) => {
        if (paragrafo.getBoundingClientRect().top <= lineaLettura) indice = i;
      });
      if (indice === indiceStoria) return;
      indiceStoria = indice;
      attivaParagrafo(indice);
    };
    const richiediStoria = () => {
      if (frameStoria) return;
      frameStoria = requestAnimationFrame(aggiornaStoria);
    };
    attivaParagrafo(0);
    window.addEventListener('scroll', richiediStoria, { passive: true });
    alRidimensionamento(richiediStoria);
    richiediStoria();
  }
}

// Luce locale sui due percorsi. È limitata al pannello sotto il puntatore e non
// influenza il layout o il resto della pagina.
if (!fermo && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.querySelectorAll('[data-spotlight]').forEach((pannello) => {
    let frame = 0;
    pannello.addEventListener('pointermove', (evento) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const box = pannello.getBoundingClientRect();
        pannello.style.setProperty('--spot-x', `${evento.clientX - box.left}px`);
        pannello.style.setProperty('--spot-y', `${evento.clientY - box.top}px`);
      });
    }, { passive: true });
  });
}

// Hero DEDO: la foto, e in futuro il video, parte dentro una maschera SVG a curve
// continue centrata sul volto. La maschera cresce fino a uscire dallo schermo;
// risalendo la pagina il cuore si ricompone senza intercettare il gesto di scroll.
const heroCuore = document.querySelector('[data-hero-cuore]');
if (heroCuore && !fermo && window.matchMedia('(min-width: 768px)').matches) {
  const scenaCuore = heroCuore.querySelector('.hero-cuore__sticky');
  const mediaCuore = heroCuore.querySelector('[data-hero-cuore-media]');
  const testataCuore = document.querySelector('[data-testata]');
  let frameCuore = 0;
  let ultimoCuore = '';

  const limita = (numero, minimo, massimo) => Math.min(massimo, Math.max(minimo, numero));
  const aggiornaCuore = () => {
    frameCuore = 0;
    if (!scenaCuore || !mediaCuore) return;
    const altezzaNav = testataCuore?.offsetHeight || 0;
    const viaggio = Math.max(1, heroCuore.offsetHeight - scenaCuore.offsetHeight);
    const progresso = limita((altezzaNav - heroCuore.getBoundingClientRect().top) / viaggio, 0, 1);
    const apertura = 1 - Math.pow(1 - progresso, 3);
    const box = scenaCuore.getBoundingClientRect();
    // Le sei scritture qui sotto dipendono solo da questi tre numeri. A cuore ormai
    // aperto restano identici per tutto il resto della pagina: riscriverli a ogni
    // fotogramma faceva ricalcolare lo stile senza cambiare un pixel.
    const cuore = `${box.width}|${box.height}|${apertura}`;
    if (cuore === ultimoCuore) return;
    ultimoCuore = cuore;
    const puntoXPercentuale = limita(Number(heroCuore.dataset.cuoreX) || 32, 0, 100);
    const puntoYPercentuale = limita(Number(heroCuore.dataset.cuoreY) || 23, 0, 100);
    const puntoX = box.width * puntoXPercentuale / 100;
    const puntoY = box.height * puntoYPercentuale / 100;
    const dimensione = limita(Number(heroCuore.dataset.cuoreDimensione) || 34, 20, 60);
    const larghezzaIniziale = limita(box.width * dimensione / 100, 280, 560);
    const larghezzaFinale = Math.hypot(box.width, box.height) * 3.6;
    const larghezzaMaschera = larghezzaIniziale + (larghezzaFinale - larghezzaIniziale) * apertura;
    const altezzaMaschera = larghezzaMaschera * 0.92;

    mediaCuore.style.webkitMaskSize = `${larghezzaMaschera.toFixed(1)}px auto`;
    mediaCuore.style.maskSize = `${larghezzaMaschera.toFixed(1)}px auto`;
    mediaCuore.style.webkitMaskPosition = `${(puntoX - larghezzaMaschera / 2).toFixed(1)}px ${(puntoY - altezzaMaschera / 2).toFixed(1)}px`;
    mediaCuore.style.maskPosition = `${(puntoX - larghezzaMaschera / 2).toFixed(1)}px ${(puntoY - altezzaMaschera / 2).toFixed(1)}px`;
    mediaCuore.style.transformOrigin = `${puntoXPercentuale}% ${puntoYPercentuale}%`;
    const scala = 1.045 - apertura * 0.045;
    mediaCuore.style.transform = `scale(${scala.toFixed(4)})`;
    heroCuore.classList.toggle('hero-cuore--schermo-pieno', apertura > 0.985);
    heroCuore.classList.toggle('hero-cuore--aperto', apertura > 0.78);
  };
  const richiediCuore = () => {
    if (frameCuore) return;
    frameCuore = requestAnimationFrame(aggiornaCuore);
  };

  window.addEventListener('scroll', richiediCuore, { passive: true });
  alRidimensionamento(richiediCuore);
  aggiornaCuore();
}

// La griglia cinetica vive soltanto nello spazio libero della hero. La fotografia
// resta un unico livello, senza filtri, duplicazioni o maschere sopra Sonia.
const telaHero = document.querySelector('[data-hero-grid]');
const heroGriglia = telaHero?.closest('[data-hero]');
if (telaHero && heroGriglia && !heroGriglia.matches('[data-hero-cuore]')) {
  const contesto = telaHero.getContext('2d');
  let larghezzaTela = 0;
  let altezzaTela = 0;
  let punti = [];
  let frameGriglia = 0;
  let inVista = true;
  let disegnabile = false;
  let ultimoPuntatore = null;

  const preparaTela = () => {
    const box = telaHero.getBoundingClientRect();
    disegnabile = box.width > 1 && box.height > 1;
    if (!disegnabile) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    larghezzaTela = Math.max(1, box.width);
    altezzaTela = Math.max(1, box.height);
    telaHero.width = Math.round(larghezzaTela * dpr);
    telaHero.height = Math.round(altezzaTela * dpr);
    contesto.setTransform(dpr, 0, 0, dpr, 0, 0);

    const colonne = Math.max(7, Math.round(larghezzaTela / 92));
    const righe = Math.max(7, Math.round(altezzaTela / 86));
    punti = [];
    for (let y = 0; y <= righe; y += 1) {
      for (let x = 0; x <= colonne; x += 1) {
        const baseX = x * larghezzaTela / colonne;
        const baseY = y * altezzaTela / righe;
        punti.push({ x: baseX, y: baseY, baseX, baseY, vx: 0, vy: 0, xGrid: x, yGrid: y, colonne, righe });
      }
    }
  };

  const disegnaGriglia = (tempo = 0) => {
    if (!disegnabile) return;
    contesto.clearRect(0, 0, larghezzaTela, altezzaTela);
    contesto.lineWidth = 1;
    contesto.strokeStyle = 'rgba(25, 18, 19, 0.17)';

    for (const punto of punti) {
      if (!fermo) {
        const onda = Math.sin(tempo * 0.0007 + punto.baseX * 0.012 + punto.baseY * 0.006) * 0.7;
        punto.vx += (punto.baseX - punto.x) * 0.022;
        punto.vy += (punto.baseY + onda - punto.y) * 0.022;
        punto.vx *= 0.89;
        punto.vy *= 0.89;
        punto.x += punto.vx;
        punto.y += punto.vy;
      } else {
        punto.x = punto.baseX;
        punto.y = punto.baseY;
      }
    }

    const trova = (x, y) => punti[y * (punti[0].colonne + 1) + x];
    for (let y = 0; y <= punti[0].righe; y += 1) {
      contesto.beginPath();
      for (let x = 0; x <= punti[0].colonne; x += 1) {
        const punto = trova(x, y);
        if (x === 0) contesto.moveTo(punto.x, punto.y);
        else contesto.lineTo(punto.x, punto.y);
      }
      contesto.stroke();
    }
    for (let x = 0; x <= punti[0].colonne; x += 1) {
      contesto.beginPath();
      for (let y = 0; y <= punti[0].righe; y += 1) {
        const punto = trova(x, y);
        if (y === 0) contesto.moveTo(punto.x, punto.y);
        else contesto.lineTo(punto.x, punto.y);
      }
      contesto.stroke();
    }

    for (const punto of punti) {
      contesto.beginPath();
      contesto.fillStyle = (punto.xGrid + punto.yGrid) % 5 === 0 ? 'rgba(191, 69, 110, 0.72)' : 'rgba(25, 18, 19, 0.34)';
      contesto.arc(punto.x, punto.y, (punto.xGrid + punto.yGrid) % 5 === 0 ? 2.1 : 1.35, 0, Math.PI * 2);
      contesto.fill();
    }

    if (!fermo && disegnabile && inVista) frameGriglia = requestAnimationFrame(disegnaGriglia);
  };

  preparaTela();
  disegnaGriglia();

  if (!fermo) {
    heroGriglia.addEventListener('pointermove', (evento) => {
      if (!disegnabile) return;
      const box = telaHero.getBoundingClientRect();
      const x = evento.clientX - box.left;
      const y = evento.clientY - box.top;
      if (x < 0 || y < 0 || x > box.width || y > box.height) {
        ultimoPuntatore = null;
        return;
      }
      const dx = ultimoPuntatore ? x - ultimoPuntatore.x : 0;
      const dy = ultimoPuntatore ? y - ultimoPuntatore.y : 0;
      const velocita = Math.min(18, Math.hypot(dx, dy) * 1.35);
      ultimoPuntatore = { x, y };
      for (const punto of punti) {
        const distanzaX = punto.x - x;
        const distanzaY = punto.y - y;
        const distanza = Math.max(1, Math.hypot(distanzaX, distanzaY));
        if (distanza > 180) continue;
        const forza = (1 - distanza / 180) * velocita;
        punto.vx += distanzaX / distanza * forza + dx * 0.035;
        punto.vy += distanzaY / distanza * forza + dy * 0.035;
      }
    }, { passive: true });
    heroGriglia.addEventListener('pointerleave', () => { ultimoPuntatore = null; });
    new IntersectionObserver(([voce]) => {
      inVista = voce.isIntersecting;
      if (inVista && disegnabile && !frameGriglia) frameGriglia = requestAnimationFrame(disegnaGriglia);
      if (!inVista && frameGriglia) {
        cancelAnimationFrame(frameGriglia);
        frameGriglia = 0;
      }
    }, { threshold: 0.08 }).observe(heroGriglia);
  }

  const ridimensionaGriglia = () => {
    if (frameGriglia) cancelAnimationFrame(frameGriglia);
    frameGriglia = 0;
    preparaTela();
    disegnaGriglia();
  };
  alRidimensionamento(ridimensionaGriglia);
}

// Caroselli finiti: gli elementi restano quelli reali del CMS, senza copie,
// riposizionamenti automatici o ritorni nascosti all'inizio della pista.
const montaPista = (pista) => {
  const contenitore = pista.closest('[data-carosello]');
  if (!contenitore) return;
  const originali = [...pista.children];
  if (!originali.length) return;
  originali.forEach((elemento, indice) => { elemento.dataset.carouselIndex = String(indice); });
  const elementi = originali;
  const attivataAlle = new WeakMap();
  let pulsantiIndicatori = [];
  let contatoreCorrente = null;
  let indiceAttivo = 0;
  let elementoAttivo = originali[0];
  let timerCentratura = 0;
  let timerFineScroll = 0;
  let trascinamento = null;

  const indiceDi = (elemento) => Number.parseInt(elemento?.dataset.carouselIndex || '0', 10);
  const aggiornaCoverflow = () => {
    if (!contenitore.matches('.programmi') || !pista.classList.contains('programmi__pista--coverflow')) return;
    const centroPista = pista.scrollLeft + pista.clientWidth / 2;
    const ampiezza = elementi[0]?.offsetWidth || 1;
    // Prima tutte le misure, poi tutte le scritture. Alternarle costringeva il browser
    // a ricalcolare il layout una volta per card, a ogni fotogramma di scorrimento.
    const distanze = elementi.map((elemento) => {
      const centroElemento = elemento.offsetLeft + elemento.offsetWidth / 2;
      return Math.max(-2.25, Math.min(2.25, (centroElemento - centroPista) / ampiezza));
    });
    elementi.forEach((elemento, i) => {
      const distanza = distanze[i];
      const distanzaAssoluta = Math.abs(distanza);
      elemento.style.setProperty('--coverflow-distanza', distanza.toFixed(4));
      elemento.style.setProperty('--coverflow-assoluta', distanzaAssoluta.toFixed(4));
      elemento.style.zIndex = String(Math.max(1, 20 - Math.round(distanzaAssoluta * 6)));
    });
  };

  if (originali.length > 1 && pista.hasAttribute('data-active-track')) {
    const navigazione = document.createElement('div');
    navigazione.className = 'carosello-navigazione';
    // Senza un ruolo l'aria-label su un <div> non viene esposto da nessuno screen reader.
    navigazione.setAttribute('role', 'group');
    navigazione.setAttribute('aria-label', 'Navigazione del carosello');

    const contatore = document.createElement('p');
    contatore.className = 'carosello-contatore';
    contatore.setAttribute('aria-live', 'polite');
    contatoreCorrente = document.createElement('span');
    const contatoreTotale = document.createElement('span');
    contatoreTotale.textContent = String(originali.length).padStart(2, '0');
    contatore.append(contatoreCorrente, document.createTextNode(' / '), contatoreTotale);

    const indicatori = document.createElement('div');
    indicatori.className = 'carosello-indicatori';
    indicatori.setAttribute('role', 'group');
    indicatori.setAttribute('aria-label', 'Scegli un elemento');
    pulsantiIndicatori = originali.map((elemento, indice) => {
      const indicatore = document.createElement('button');
      indicatore.className = 'carosello-indicatore';
      indicatore.type = 'button';
      indicatore.setAttribute('aria-label', `Vai all'elemento ${indice + 1} di ${originali.length}`);
      indicatore.addEventListener('click', () => centraIndice(indice));
      indicatori.append(indicatore);
      return indicatore;
    });

    navigazione.append(contatore, indicatori);
    pista.insertAdjacentElement('afterend', navigazione);
  }

  const impostaAttivo = (elemento) => {
    if (!pista.hasAttribute('data-active-track') || !elemento) return;
    indiceAttivo = indiceDi(elemento);
    elementoAttivo = elemento;
    elementi.forEach((voce) => {
      const diventaAttiva = voce === elemento;
      if (diventaAttiva && !voce.classList.contains('is-active')) attivataAlle.set(voce, performance.now());
      voce.classList.toggle('is-active', diventaAttiva);
    });
    aggiornaCoverflow();
    pulsantiIndicatori.forEach((indicatore, indice) => {
      if (indice === indiceAttivo) indicatore.setAttribute('aria-current', 'true');
      else indicatore.removeAttribute('aria-current');
    });
    if (contatoreCorrente) contatoreCorrente.textContent = String(indiceAttivo + 1).padStart(2, '0');
  };

  const aggiornaAttivo = () => {
    if (!pista.hasAttribute('data-active-track') || !elementi.length) return;
    const centroPista = pista.scrollLeft + pista.clientWidth / 2;
    const centrale = elementi.reduce((migliore, elemento) => {
      const distanza = Math.abs(elemento.offsetLeft + elemento.offsetWidth / 2 - centroPista);
      return !migliore || distanza < migliore.distanza ? { elemento, distanza } : migliore;
    }, null);
    impostaAttivo(centrale?.elemento);
    aggiornaCoverflow();
  };

  const centra = (elemento, comportamento = 'smooth') => {
    if (!elemento) return;
    if (timerCentratura) window.clearTimeout(timerCentratura);
    impostaAttivo(elemento);
    const massimo = Math.max(0, pista.scrollWidth - pista.clientWidth);
    const destinazione = elemento.offsetLeft + elemento.offsetWidth / 2 - pista.clientWidth / 2;
    pista.scrollTo({
      left: Math.min(massimo, Math.max(0, destinazione)),
      behavior: fermo ? 'auto' : comportamento
    });
    timerCentratura = window.setTimeout(() => {
      timerCentratura = 0;
      aggiornaAttivo();
      aggiornaFrecce();
    }, fermo || comportamento === 'auto' ? 0 : 760);
  };

  const centraIndice = (indice, comportamento = 'smooth') => {
    const limitato = Math.max(0, Math.min(originali.length - 1, indice));
    centra(originali[limitato], comportamento);
  };

  if (pista.hasAttribute('data-active-track') && elementi.length) {
    impostaAttivo(originali[0]);
    let frameAttivo = 0;
    pista.addEventListener('scroll', () => {
      if (frameAttivo) cancelAnimationFrame(frameAttivo);
      frameAttivo = requestAnimationFrame(() => {
        frameAttivo = 0;
        aggiornaCoverflow();
        if (!timerCentratura) aggiornaAttivo();
      });
      if (timerCentratura) return;
      if (timerFineScroll) window.clearTimeout(timerFineScroll);
      timerFineScroll = window.setTimeout(() => {
        aggiornaAttivo();
      }, 140);
    }, { passive: true });
    alRidimensionamento(() => {
      aggiornaAttivo();
      aggiornaCoverflow();
    });

    // Da tastiera il fuoco arriva anche sulle card laterali, ma l'Invio su un link
    // produce lo stesso evento di click del mouse: finiva nel preventDefault qui sotto
    // e non apriva niente. Centrando la card appena la si mette a fuoco, quando l'Invio
    // arriva la card è già attiva e il link parte. Il filtro su :focus-visible tiene
    // fuori il mouse, così il primo click continua a centrare e il secondo a seguire.
    pista.addEventListener('focusin', (evento) => {
      const bersaglio = evento.target;
      if (typeof bersaglio.matches !== 'function' || !bersaglio.matches(':focus-visible')) return;
      const elemento = elementi.find((voce) => voce.contains(bersaglio));
      if (!elemento || elemento.classList.contains('is-active')) return;
      centra(elemento);
    });

    // Un click su una card laterale la porta al centro. Sulle card prodotto il
    // primo click seleziona, il secondo segue il link già attivo.
    let annullaClick = false;
    pista.addEventListener('click', (evento) => {
      if (annullaClick) {
        evento.preventDefault();
        annullaClick = false;
        return;
      }
      const elemento = elementi.find((voce) => voce.contains(evento.target));
      if (!elemento) return;
      if (elemento.classList.contains('is-active')) {
        const appenaAttivata = performance.now() - (attivataAlle.get(elemento) || 0) < 650;
        if (contenitore.matches('.programmi') && appenaAttivata) evento.preventDefault();
        return;
      }
      evento.preventDefault();
      centra(elemento);
    });

    // Drag-to-pan per mouse e penna. Sul touch resta lo scorrimento nativo del
    // browser, più affidabile per il gesto verticale della pagina.
    pista.addEventListener('pointerdown', (evento) => {
      if (evento.pointerType === 'touch' || evento.button !== 0) return;
      if (timerCentratura) window.clearTimeout(timerCentratura);
      timerCentratura = 0;
      trascinamento = { id: evento.pointerId, x: evento.clientX, scroll: pista.scrollLeft, mosso: false };
    });
    pista.addEventListener('pointermove', (evento) => {
      if (!trascinamento || trascinamento.id !== evento.pointerId) return;
      const delta = evento.clientX - trascinamento.x;
      if (Math.abs(delta) > 6 && !trascinamento.mosso) {
        trascinamento.mosso = true;
        pista.setPointerCapture(evento.pointerId);
        pista.classList.add('is-dragging');
      }
      if (!trascinamento.mosso) return;
      pista.scrollLeft = trascinamento.scroll - delta;
    });
    const terminaTrascinamento = (evento) => {
      if (!trascinamento || trascinamento.id !== evento.pointerId) return;
      annullaClick = trascinamento.mosso;
      trascinamento = null;
      pista.classList.remove('is-dragging');
      if (pista.hasPointerCapture(evento.pointerId)) pista.releasePointerCapture(evento.pointerId);
      aggiornaAttivo();
      if (elementoAttivo) centra(elementoAttivo);
    };
    pista.addEventListener('pointerup', terminaTrascinamento);
    pista.addEventListener('pointercancel', terminaTrascinamento);

  }

  const aggiornaFrecce = () => {
    const max = pista.scrollWidth - pista.clientWidth - 1;
    contenitore.querySelectorAll('[data-scorri]').forEach((b) => {
      const avanti = b.dataset.scorri === 'avanti';
      b.disabled = pista.hasAttribute('data-active-track')
        ? (avanti ? indiceAttivo >= originali.length - 1 : indiceAttivo <= 0)
        : (avanti ? pista.scrollLeft >= max : pista.scrollLeft <= 1);
    });
  };

  contenitore.querySelectorAll('[data-scorri]').forEach((bottone) => {
    bottone.addEventListener('click', () => {
      const incremento = bottone.dataset.scorri === 'avanti' ? 1 : -1;
      centraIndice(indiceAttivo + incremento);
    });
  });

  pista.addEventListener('scroll', aggiornaFrecce, { passive: true });
  alRidimensionamento(aggiornaFrecce);
  aggiornaFrecce();
  if (pista.hasAttribute('data-active-track')) requestAnimationFrame(() => {
    centra(originali[0], 'auto');
    aggiornaCoverflow();
  });
};

document.querySelectorAll('[data-pista]').forEach(montaPista);

// Nell'editor Shopify la sezione viene ridisegnata a ogni modifica, quindi le piste
// montate al caricamento non esistono più: senza questa riga, chi aggiunge un confronto
// vede le frecce smettere di rispondere finché non ricarica la pagina. L'evento lo emette
// solo l'editor, sul sito pubblicato non arriva mai.
document.addEventListener('shopify:section:load', (evento) => {
  evento.target.querySelectorAll('[data-pista]').forEach(montaPista);
});

// 3. Menu mobile. Pannello modale: `inert` sotto, così il Tab non ne esce. Perché `inert`
// e non showModal(): §4 di docs/03-standard-di-lavoro.md.
const attiva = document.querySelector('[data-apri-menu]');
const pannello = document.querySelector('[data-menu]');
if (attiva && pannello) {
  const fuori = document.querySelectorAll('main, footer, [data-inerte]');
  const nomeAttiva = attiva.querySelector('.solo-sr');
  const commuta = (aperto) => {
    attiva.setAttribute('aria-expanded', String(aperto));
    // `aria-expanded` dice lo stato, ma il nome del pulsante continuava a dire «Apri»
    // anche a menu aperto, e il nome è la prima cosa che viene letta.
    if (nomeAttiva) nomeAttiva.textContent = aperto ? 'Chiudi il menu' : 'Apri il menu';
    pannello.hidden = !aperto;
    document.body.style.overflow = aperto ? 'hidden' : '';
    for (const n of fuori) n.inert = aperto;
    if (aperto) requestAnimationFrame(() => pannello.querySelector('a, button')?.focus());
  };
  attiva.addEventListener('click', () => commuta(attiva.getAttribute('aria-expanded') !== 'true'));
  pannello.addEventListener('click', (e) => { if (e.target.closest('a')) commuta(false); });
  pannello.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { commuta(false); attiva.focus(); return; }
    if (e.key !== 'Tab') return;
    const voci = [...pannello.querySelectorAll('a, button')].filter((n) => !n.disabled);
    if (!voci.length) return;
    const primo = voci[0], ultimo = voci[voci.length - 1];
    if (e.shiftKey && document.activeElement === primo) { e.preventDefault(); ultimo.focus(); }
    if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primo.focus(); }
  });
}

// Ombra della navbar solo quando la pagina è scrollata.
const testata = document.querySelector('[data-testata]');
if (testata) {
  const sentinella = document.createElement('div');
  testata.before(sentinella);
  new IntersectionObserver(
    ([v]) => testata.classList.toggle('testata--staccata', !v.isIntersecting)
  ).observe(sentinella);
}

// 3.1 Ritorno in cima. Compare dopo quasi un viewport di scroll; il moto lo dà
// `scroll-behavior` del CSS, qui si passa solo la destinazione.
const tornaSu = document.querySelector('[data-torna-su]');
if (tornaSu) {
  let inCoda = false;
  const aggiornaTornaSu = () => {
    inCoda = false;
    tornaSu.classList.toggle('torna-su--visibile', window.scrollY > window.innerHeight * 0.8);
  };
  window.addEventListener('scroll', () => {
    if (inCoda) return;
    inCoda = true;
    requestAnimationFrame(aggiornaTornaSu);
  }, { passive: true });
  aggiornaTornaSu();

  tornaSu.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: fermo ? 'auto' : 'smooth' });
    // Chi naviga da tastiera deve ripartire dall'inizio del documento, non da qui.
    document.querySelector('.testata__logo')?.focus({ preventScroll: true });
  });
}

// 4. Copia del codice sconto. Senza JS il codice resta selezionabile: il pulsante è un di più.
document.querySelectorAll('[data-copia]').forEach((bottone) => {
  bottone.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(bottone.dataset.copia);
    } catch {
      return; // clipboard negata: il codice è comunque lì da selezionare
    }
    // L'icona cambia via CSS. Cambiare l'aria-label del pulsante che ha il fuoco non
    // viene riletto in modo affidabile, quindi la conferma passa da una regione live.
    let annuncio = bottone.parentElement.querySelector('[data-copia-esito]');
    if (!annuncio) {
      annuncio = document.createElement('span');
      annuncio.className = 'solo-sr';
      annuncio.dataset.copiaEsito = '';
      annuncio.setAttribute('role', 'status');
      bottone.after(annuncio);
    }
    annuncio.textContent = 'Codice copiato';
    bottone.dataset.fatto = '';
    setTimeout(() => { annuncio.textContent = ''; delete bottone.dataset.fatto; }, 2000);
  });
});

// 5. Facade YouTube: iframe montato al clic e su dominio senza cookie, mai al
// caricamento — altrimenti il sito si compra un banner cookie. Ticket 11.
document.querySelectorAll('[data-youtube]').forEach((f) => {
  f.querySelector('button').addEventListener('click', () => {
    const i = document.createElement('iframe');
    // Il codice arriva dall'admin già ripulito dal Liquid. `encodeURIComponent` è la
    // seconda serratura: qualunque cosa ci sia scritta resta un pezzo di percorso e non
    // può aggiungere parametri o cambiare la destinazione dell'indirizzo.
    i.src = 'https://www.youtube-nocookie.com/embed/' +
            encodeURIComponent(f.dataset.youtube) + '?autoplay=1';
    i.title = f.dataset.titolo || 'Video';
    i.allow = 'autoplay; encrypted-media; fullscreen';
    f.replaceChildren(i);
    // Il pulsante appena premuto sparisce dal documento: senza questa riga il fuoco
    // torna al <body> e da tastiera si riparte dall'inizio della pagina.
    i.focus();
  });
});
